/**
 * Rebuilds a chat session from the backend's own turn ledger, so a page
 * reload redraws the pane instead of opening it empty.
 *
 * Pure — no `@assistant-ui/*` import here. `ask-profile-thread.tsx` is the
 * only file allowed to touch that package; this module returns plain objects
 * shaped to match `ThreadMessageLike` and `AskProfileAnswerData` by
 * structure, so the thread component can hand them straight to
 * `useLocalRuntime`'s `initialMessages` without this file knowing the type
 * exists.
 */

import {
  buildSlideQuestion,
  PRESET_QUESTIONS,
  type PresetQuestionKey,
} from '../config/preset-questions'
import type { ChatSource } from '../types/chat.types'

/** One earlier turn, exactly as `POST …/chat/history` returns it. */
export interface ProfileChatHistoryTurn {
  kind: 'PRESET' | 'TYPED' | 'FOLLOWUP' | 'SLIDE' | 'REFUSAL'
  presetKey: PresetQuestionKey | null
  slideNumber: number | null
  questionText: string
  answerText: string | null
  sources: ChatSource[]
  followUps: string[]
}

/**
 * What the investor actually saw in their own bubble — never `questionText`
 * for a PRESET or SLIDE turn, which is the model's prompt body, not the short
 * label the chip or the deck row showed. Reusing `PRESET_QUESTIONS` and
 * `buildSlideQuestion` is what keeps this from drifting against the live chips:
 * one definition of each label, read by both the asking path and this one.
 */
export function reconstructQuestionText(turn: ProfileChatHistoryTurn): string {
  if (turn.kind === 'PRESET' && turn.presetKey) {
    const preset = PRESET_QUESTIONS.find((question) => question.key === turn.presetKey)
    if (preset) return preset.transcriptQuestion
  }
  if (turn.kind === 'SLIDE' && turn.slideNumber !== null) {
    return buildSlideQuestion(turn.slideNumber)
  }
  return turn.questionText
}

const ENTRY_BY_KIND: Record<
  ProfileChatHistoryTurn['kind'],
  'chip' | 'typed' | 'slide' | 'followup'
> = {
  PRESET: 'chip',
  SLIDE: 'slide',
  FOLLOWUP: 'followup',
  TYPED: 'typed',
  REFUSAL: 'typed',
}

/** A plain object shaped like assistant-ui's `ThreadMessageLike`. */
export interface HistoryMessageLike {
  role: 'user' | 'assistant'
  content: readonly { type: 'text'; text: string }[]
  metadata?: { custom: Record<string, unknown> }
}

/**
 * One user/assistant bubble pair per turn, in order. A turn with no answer
 * (abandoned before it finished) contributes only its question — an empty
 * assistant bubble would render nothing useful and `AskProfileAnswer` already
 * treats empty content as "nothing to frame".
 */
export function buildHistoryMessages(
  turns: readonly ProfileChatHistoryTurn[],
  chatSessionId: string
): HistoryMessageLike[] {
  const messages: HistoryMessageLike[] = []
  for (const turn of turns) {
    const text = reconstructQuestionText(turn)
    messages.push({ role: 'user', content: [{ type: 'text', text }] })
    if (turn.answerText === null || turn.answerText.trim() === '') continue
    messages.push({
      role: 'assistant',
      content: [{ type: 'text', text: turn.answerText }],
      metadata: {
        custom: {
          askProfile: {
            turn: {
              entry: ENTRY_BY_KIND[turn.kind],
              text,
              presetKey: turn.presetKey ?? undefined,
              slideNumber: turn.slideNumber ?? undefined,
              clientTurnId: crypto.randomUUID(),
              chatSessionId,
            },
            sources: turn.sources,
            followUps: turn.followUps,
            phase: 'done',
            errorMessage: null,
            errorCode: null,
            citationsPending: [],
            citationVerdicts: {},
          },
        },
      },
    })
  }
  return messages
}
